import { useState } from "react";
import { formatINR } from "@garage/shared";
import { downloadReport, useSummary, type ReportRange } from "../api/reports";
import { useVehicles } from "../api/vehicles";
import { Alert, Button, Card, Field, inputClass, Label, Spinner } from "../components/ui";
import { presets } from "../lib/ranges";

export default function ReportsPage() {
  const options = presets();
  const vehicles = useVehicles();

  const [presetId, setPresetId] = useState("fy");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [vehicleId, setVehicleId] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const range: ReportRange =
    presetId === "custom"
      ? { from: custom.from || undefined, to: custom.to || undefined }
      : (options.find((p) => p.id === presetId)?.range ?? {});

  const scoped: ReportRange = { ...range, ...(vehicleId ? { vehicleId } : {}) };
  const preview = useSummary(scoped);

  async function download(kind: "vehicle" | "overall") {
    setBusy(kind);
    setError(null);
    try {
      await downloadReport(
        kind === "vehicle"
          ? `/api/reports/vehicle/${vehicleId}.pdf`
          : "/api/reports/overall.pdf",
        scoped,
        kind === "vehicle" ? "vehicle-report.pdf" : "vehicle-spend.pdf",
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
      <p className="mt-1 text-ink-2">
        Download a PDF of what you spent, for a vehicle or across the garage.
      </p>

      <Card className="mt-6 flex flex-col gap-5 p-5">
        <div>
          <Label>Period</Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[...options, { id: "custom", label: "Custom", range: {} }].map((p) => (
              <button
                key={p.id}
                onClick={() => setPresetId(p.id)}
                aria-pressed={p.id === presetId}
                className={`rounded border px-2.5 py-1 font-mono text-[11px] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-signal ${
                  p.id === presetId
                    ? "border-signal bg-signal/15 text-signal"
                    : "border-line text-ink-3 hover:text-ink"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {presetId === "custom" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="From">
              <input
                className={inputClass}
                type="date"
                value={custom.from}
                onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
              />
            </Field>
            <Field label="To">
              <input
                className={inputClass}
                type="date"
                value={custom.to}
                onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
              />
            </Field>
          </div>
        )}

        <Field label="Vehicle" hint="Leave on all vehicles for the overall report">
          <select
            className={inputClass}
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
          >
            <option value="">All vehicles</option>
            {vehicles.data?.vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.nickname || v.regNumber}
              </option>
            ))}
          </select>
        </Field>
      </Card>

      <Card className="mt-4 p-5">
        <Label>What is in it</Label>
        <div className="mt-3">
          {preview.isPending && <Spinner label="Checking the period" />}
          {preview.data && (
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <Stat label="Services" value={String(preview.data.totals.services)} />
              <Stat label="Total" value={formatINR(preview.data.totals.spend)} />
              <Stat label="Average" value={formatINR(preview.data.totals.avgCost)} />
              {preview.data.costPerKm && (
                <Stat label="Cost / km" value={formatINR(preview.data.costPerKm.value)} />
              )}
            </div>
          )}
        </div>
      </Card>

      {error && <div className="mt-4"><Alert>{error}</Alert></div>}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          onClick={() => download("overall")}
          disabled={busy !== null || preview.data?.totals.services === 0}
        >
          {busy === "overall" ? "Generating…" : "Download overall report"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => download("vehicle")}
          disabled={busy !== null || !vehicleId || preview.data?.totals.services === 0}
          title={!vehicleId ? "Pick a vehicle first" : undefined}
        >
          {busy === "vehicle" ? "Generating…" : "Download vehicle report"}
        </Button>
      </div>

      {preview.data?.totals.services === 0 && (
        <p className="mt-3 text-sm text-ink-3">
          Nothing logged in this period, so there is no report to generate.
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[10px] tracking-[0.1em] text-ink-3 uppercase">
        {label}
      </p>
      <p className="mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}
